import {
  Box,
  Skeleton,
  Stack,
  Typography,
  darken,
  useMediaQuery,
} from "@mui/material";
import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { startOfDay, isWithinInterval } from "date-fns";
import { times } from "lodash";
import { useCallback, useMemo } from "react";
import TimeSlotGrid from "@/components/booking/timeSlotGrid";
import { GREY, VIOLET } from "@/config/mui/colorPalette";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import { TimeSlot } from "@/types/bookingTypes";
import {
  getLatestBookingDate,
  getEarliestBookingDate,
} from "@/utils/getBookingRangeDates";
import { getDateString } from "@/utils/visit";
import {
  ChevronSmallDownIcon,
  ChevronSmallLeftIcon,
  ChevronSmallRightIcon,
} from "../common/icons";

type Props = {
  selectedTimeSlot: TimeSlot | null;
  onDateChange: (date: Date) => void;
  onTimeSlotSelect: (timeSlot: TimeSlot) => void;
  onMonthChange: (date: Date) => void;
  date: Date;
  bookingAvailableDates: Array<string>;
  timeSlots: Array<TimeSlot>;
  medspaTimeZone: string;
  medspaName: string;
  maximumAdvanceBookingHours: number;
  customizationPrimaryColor?: string;
  customizationTextColor?: string;
  loading: boolean;
  startDateSelectiveBookingLink?: string;
  endDateSelectiveBookingLink?: string;
};

const CALENDAR_CONSTANTS = {
  DATE_SIZE: 50,
  CALENDAR_SIZE: {
    WIDTH: 400,
    HEIGHT: 425,
  },
} as const;

const getCalendarStylesByScreenSize = (
  isCompactScreen: boolean,
  isWideScreen: boolean,
  customizationPrimaryColor?: string,
  customizationTextColor?: string
) => ({
  ...(customizationPrimaryColor && customizationTextColor
    ? {
        "& .MuiPickersDay-root.Mui-selected": {
          backgroundColor: `${customizationPrimaryColor} !important`,
          color: customizationTextColor,
        },
        "& .MuiPickersDay-root.Mui-selected:hover": {
          backgroundColor: `${darken(customizationPrimaryColor, 0.2)} !important`,
        },
      }
    : {}),
  "& .MuiPickersCalendarHeader-root": {
    paddingRight: 0,
    ...(isCompactScreen
      ? {
          paddingLeft: 1.5,
          paddingRight: 3,
        }
      : {}),
    ...(isWideScreen
      ? {
          paddingRight: 3,
        }
      : {}),
  },

  "& .MuiPickersLayout-root": {
    ...(isCompactScreen
      ? {
          minWidth: 280,
        }
      : {}),
    ...(isWideScreen
      ? {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }
      : {}),
  },
  "& .MuiDateCalendar-root": {
    border: `1px solid ${GREY[20]}`,
    borderRadius: "12px",
    ...(isCompactScreen
      ? {
          width: 280,
          ml: 0,
          border: "none",
          borderRadius: "12px",
        }
      : {}),
    ...(isWideScreen
      ? {
          width: CALENDAR_CONSTANTS.CALENDAR_SIZE.WIDTH,
          height: CALENDAR_CONSTANTS.CALENDAR_SIZE.HEIGHT,
          overflow: "visible",
          maxHeight: CALENDAR_CONSTANTS.CALENDAR_SIZE.HEIGHT,
        }
      : {}),
  },
  "& .MuiDayCalendar-header": {
    ...(isCompactScreen
      ? {
          justifyContent: "flex-start",
        }
      : {}),
  },
  "& .MuiPickersSlideTransition-root": {
    ...(isCompactScreen
      ? {
          width: "initial",
          height: "initial",
          overflow: "visible",
          maxHeight: "initial",
        }
      : {}),
    ...(isWideScreen
      ? {
          minHeight: 330,
        }
      : {}),
  },

  "& .MuiDayCalendar-monthContainer > div": {
    ...(isCompactScreen
      ? {
          justifyContent: "flex-start",
        }
      : {}),
  },

  ...(isWideScreen
    ? {
        "& .MuiTypography-caption": {
          width: CALENDAR_CONSTANTS.DATE_SIZE,
          margin: 0,
        },
        "& .MuiPickersDay-dayWithMargin": {
          margin: 0,
        },
        "& .MuiPickersDay-root": {
          margin: "4px",
          width: CALENDAR_CONSTANTS.DATE_SIZE - 8,
          height: CALENDAR_CONSTANTS.DATE_SIZE - 8,
        },
      }
    : {}),

  ...(isCompactScreen
    ? {
        ml: -1,
      }
    : {}),
});

export default function DateTimeSelectionSlotGrid({
  selectedTimeSlot,
  onDateChange,
  onTimeSlotSelect,
  onMonthChange,
  date,
  bookingAvailableDates,
  timeSlots,
  medspaTimeZone,
  medspaName,
  maximumAdvanceBookingHours,
  customizationPrimaryColor,
  customizationTextColor,
  loading,
  startDateSelectiveBookingLink,
  endDateSelectiveBookingLink,
}: Props) {
  const isWideScreen = useMoxieMediaQuery("ipad-hor");
  const isCompactScreen = useMediaQuery("(max-width:350px)");

  const availabilityStartDate = getEarliestBookingDate({
    startDate: startDateSelectiveBookingLink,
  });
  const availabilityEndDate = getLatestBookingDate({
    maximumAdvanceBookingHours,
    endDate: endDateSelectiveBookingLink,
  });

  const isDateAvailable = useCallback(
    (date: Date) => {
      const start = startOfDay(availabilityStartDate);
      const end = startOfDay(availabilityEndDate);

      if (start > end) {
        return false;
      }

      return (
        bookingAvailableDates.includes(getDateString(date)) &&
        isWithinInterval(startOfDay(date), { start, end })
      );
    },
    [bookingAvailableDates, availabilityStartDate, availabilityEndDate]
  );

  const isDateDisabled = useCallback(
    (date: Date) => !bookingAvailableDates.includes(getDateString(date)),
    [bookingAvailableDates]
  );

  const calendarStyles = useMemo(
    () =>
      getCalendarStylesByScreenSize(
        isCompactScreen,
        isWideScreen,
        customizationPrimaryColor,
        customizationTextColor
      ),
    [
      isCompactScreen,
      isWideScreen,
      customizationPrimaryColor,
      customizationTextColor,
    ]
  );

  return (
    <>
      <Typography
        variant="h3"
        sx={{
          color: "text.primary",
          pb: 3,
        }}
      >
        Select date & time
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            sm: "1fr",
            lg: "repeat(2, 1fr)",
          },
          columnGap: 3,
          rowGap: 2,
        }}
      >
        <Box>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
              <StaticDatePicker
                maxDate={availabilityEndDate}
                minDate={availabilityStartDate}
                disablePast
                displayStaticWrapperAs="desktop"
                onChange={onDateChange}
                onMonthChange={(date) => onMonthChange(date)}
                value={date}
                sx={calendarStyles}
                disableHighlightToday
                slots={{
                  switchViewButton: () => <ChevronSmallDownIcon />,
                  leftArrowIcon: (props) => <ChevronSmallLeftIcon {...props} />,
                  rightArrowIcon: (props) => (
                    <ChevronSmallRightIcon {...props} />
                  ),
                }}
                slotProps={{
                  day: (dayProps) => {
                    const borderColor = dayProps.selected
                      ? VIOLET[90]
                      : VIOLET[80];

                    return {
                      sx: {
                        border: isDateAvailable(dayProps.day as Date)
                          ? `1px solid ${borderColor}`
                          : "none",
                        "&:hover": {
                          border: `1px solid ${VIOLET[90]}`,
                        },
                      },
                    };
                  },
                }}
                loading={loading}
                renderLoading={() => (
                  <CalendarLoader isBigScreen={isWideScreen} />
                )}
                shouldDisableDate={isDateDisabled}
              />
            </Box>
          </LocalizationProvider>
        </Box>
        {isDateAvailable(date) && (
          <Box sx={{ px: { xs: 3, lg: 0 } }}>
            <TimeSlotGrid
              selectedTimeSlot={selectedTimeSlot}
              onTimeSlotSelect={onTimeSlotSelect}
              timeSlots={timeSlots}
              customizationPrimaryColor={customizationPrimaryColor}
            />
            <Typography
              variant="paragraphSmall"
              sx={{
                color: "secondary.main",
              }}
            >
              Please note that you are booking within {medspaName} timezone{" "}
              <strong>{medspaTimeZone.replace("_", " ")}</strong>
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
}

function CalendarLoader({ isBigScreen }: { isBigScreen: boolean }) {
  const dimensions = isBigScreen
    ? {
        width: 330,
        height: 44,
      }
    : {
        width: 260,
        height: 30,
      };

  return (
    <Stack spacing={1}>
      {times(6, (i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          sx={{ borderRadius: 2 }}
          {...dimensions}
        />
      ))}
    </Stack>
  );
}
